import { faDatabase, faFile, faSparkles, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { PERSON_UPLOAD_CREDITS } from "@signalist/shared-types";

import { ListSourceType } from "@/enum/list.enum";
import { ReactComponent as EventIcon } from "@assets/icons/create-list/eventIcon.svg";
import { ReactComponent as Linkedin } from "@assets/icons/create-list/linkedin.svg";
import { ReactComponent as MapIcon } from "@assets/icons/create-list/maps.svg";
import { ReactComponent as UploadFile } from "@assets/icons/create-list/uploadFile.svg";

import { ReactComponent as Exhibitors } from "@assets/icons/create-list/exhibitors.svg";
import { ReactComponent as JobOffer } from "@assets/icons/create-list/jobOffer.svg";
import { ReactComponent as JobSource } from "@assets/icons/create-list/jobSource.svg";
import { ReactComponent as LikeIcon } from "@assets/icons/create-list/likeIcon.svg";
import { ReactComponent as LinkedinEvent } from "@assets/icons/create-list/linkedinEvent.svg";
import { ReactComponent as LISN } from "@assets/icons/create-list/lisn.svg";
import { ReactComponent as Person } from "@assets/icons/create-list/person.svg";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { CustomEventType } from "@/enum/custom-event.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { LiPostType } from "@/enum/liprofile.enum";
import { LiEventType } from "@/enum/lievent.enum";
import { LiPeopleType } from "@/enum/li-people.enum";
import { LiSnType } from "@/enum/li-sn.enum";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";
import { FundingMaType } from "@/enum/funding-ma.enum";
import { CompanyEnrichType } from "@/enum/company-enrich-type.enum";

import { liEventTags, liPeopleTags, liSnTags } from "./tileTags";

/**
 * Mode of a source form (the "type" the user picks *inside* a source's config form,
 * e.g. LiPost BY_TOPIC vs BY_URL). Surfacing these as flat catalog tiles lets the
 * catalog grid + the Ask-AI recommender offer the sub-types directly, and the chosen
 * mode is deep-linked into the source form via the `mode` URL param (see `sourceMode.ts`).
 */
export type SourceMode =
	| LiPostType
	| LiEventType
	| LiPeopleType
	| LiSnType
	| ParallelAiType
	| FundingMaType
	| CompanyEnrichType;

export interface Tile {
	listType: ListSourceType;
	subType?: CsvEnrichmentType | CustomEventType | ScrapeLevel;
	mode?: SourceMode;
	title: string;
	description: string;
	note?: string;
	icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
	secondIcon?: IconDefinition;
	disabled?: boolean;
	credits?: string;
	per?: string;
	tags?: {
		label: string;
		information?: string;
	}[];
	requires?: string;
	subPer?: string | undefined;
}

export interface CategoryTile {
	title: string;
	description: string;
	icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
	secondIcon?: IconDefinition;
	items: Tile[];
	requires?: string;
}

export const createNewListSettings: CategoryTile[] = [
	{
		title: "Upload an XLSX file / Fill via webhook",
		description:
			"Upload a list of people, companies or job titles to find enriched leads or add new ones via webhooks",
		icon: UploadFile,
		items: [
			{
				listType: ListSourceType.CSV_ENRICHMENTS,
				title: "Upload a list of people",
				description: `Upload an XLSX with names of people — event attendees, CRM contacts — and we'll find their LinkedIn profiles, emails and phone numbers. ${PERSON_UPLOAD_CREDITS} credit${PERSON_UPLOAD_CREDITS === 1 ? "" : "s"} per person searched, also when no one is found.`,
				icon: UploadFile,
				credits: `${PERSON_UPLOAD_CREDITS}`,
				per: "person searched",
				subPer: "Charged also when no one is found",
				subType: CsvEnrichmentType.PERSON,
			},
			{
				listType: ListSourceType.CSV_ENRICHMENTS,
				title: "Upload a list of companies",
				description:
					"Upload an XLSX with names of companies — exhibitor or target-account lists — and we'll find relevant leads at each one.",
				icon: UploadFile,
				credits: "1",
				per: "Person",
				subType: CsvEnrichmentType.ORGANIZATION,
			},
			{
				listType: ListSourceType.CSV_ENRICHMENTS,
				title: "Upload a list of job titles",
				description: `Upload an XLSX with job titles and company names and we'll find matching people, their LinkedIn profiles, emails and phone numbers. ${PERSON_UPLOAD_CREDITS} credit${PERSON_UPLOAD_CREDITS === 1 ? "" : "s"} per person searched, also when no one is found.`,
				icon: UploadFile,
				credits: `${PERSON_UPLOAD_CREDITS}`,
				per: "person searched",
				subPer: "Charged also when no one is found",
				subType: CsvEnrichmentType.PERSON_BY_TITLE,
			},
		],
	},
	{
		title: "Scraping as a Service",
		description:
			"Request one of our developers to scrape participant lists, exhibitor lists or any other website or mobile app",
		icon: EventIcon,
		requires: "WEBSITE_SCRAPE_CREDITS",
		items: [
			{
				listType: ListSourceType.CUSTOM_EVENTS,
				title: "Request us to scrape a list of people",
				description:
					"Have our team scrape a list of people, such as event attendees — in web apps, mobile apps, including behind a login.",
				icon: Person,
				disabled: false,
				credits: "1",
				per: "person",
				subType: CustomEventType.PERSON,
				requires: "WEBSITE_SCRAPE_CREDITS",
				tags: [
					{
						label: "Requires your website login",
						information:
							"This is only the case if the website requires a login to show the list of attendees",
					},
				],
			},
			{
				listType: ListSourceType.CUSTOM_EVENTS,
				title: "Request us to scrape a list of companies",
				description:
					"Have our team scrape a list of companies, such as exhibitor lists — in web apps, mobile apps, including behind a login.",
				icon: Exhibitors,
				disabled: false,
				credits: "1",
				per: "Person",
				subType: CustomEventType.ORGANIZATION,
				requires: "WEBSITE_SCRAPE_CREDITS",
				tags: [
					{
						label: "Requires your website login",
						information:
							"This is only the case if the website requires a login to show the list of exhibitors",
					},
				],
			},
		],
	},
	{
		title: "Find people based on their Linkedin activities",
		description:
			"Find people who post or engage with content, join Linkedin events or visit or follow your profiles",
		icon: Linkedin,
		items: [
			{
				listType: ListSourceType.LI_PROFILE_VISITS,
				title: "Track my profile visitors",
				description: "See who's been visiting your personal LinkedIn profile.",
				icon: Person,
				disabled: false,
				credits: "1",
				per: "Person",
				tags: [
					{
						label: "Requires LinkedIn Premium",
					},
					{
						label: "Uses your Linkedin account",
					},
				],
			},
			{
				listType: ListSourceType.LI_COMPANY_ACTIVITY,
				title: "Track new company page followers",
				description:
					"See who's started following your LinkedIn company page. Requires admin rights on the page.",
				icon: Exhibitors,
				disabled: false,
				credits: "1",
				per: "Person",
				tags: [
					{
						label: "Requires Admin Rights",
					},
					{
						label: "Uses your Linkedin account",
					},
				],
			},
			{
				listType: ListSourceType.LI_EVENTS,
				mode: LiEventType.SPECIFIC,
				title: "Find Linkedin event attendees",
				description: "Pull the full attendee list from LinkedIn events like webinars.",
				icon: LinkedinEvent,
				disabled: false,
				credits: "1",
				per: "Person",
				tags: liEventTags,
			},
			{
				listType: ListSourceType.LI_POST,
				mode: LiPostType.BY_TOPIC,
				title: "Find people posting about a topic",
				description: "Find people posting about any keyword or topic on LinkedIn.",
				icon: LikeIcon,
				disabled: false,
				credits: "1",
				per: "Person",
			},
			{
				listType: ListSourceType.LI_POST,
				mode: LiPostType.BY_URL,
				title: "Find people engaging with specific posts",
				description: "Pull everyone who liked or commented a specific LinkedIn post.",
				icon: LikeIcon,
				disabled: false,
				credits: "1",
				per: "Person",
			},
			{
				listType: ListSourceType.LI_POST,
				mode: LiPostType.BY_PROFILE,
				title: "Find people engaging with Linkedin profiles",
				description: "Watch specific LinkedIn profiles and capture the engagement on their posts.",
				icon: LikeIcon,
				disabled: false,
				credits: "1",
				per: "Person",
			},
		],
	},
	{
		title: "Import leads from Linkedin, Google Maps or our Lead Database",
		description:
			"Find leads via Linkedin Search, Linkedin Sales Navigator, Google Maps, Funding Rounds & M&A Activities or our Lead Database",
		icon: Linkedin,
		items: [
			{
				listType: ListSourceType.COMPANY_ENRICH,
				mode: CompanyEnrichType.SEARCH,
				title: "Search the lead database",
				description:
					"Filter our database by company and people criteria — size, HQ location, industry and many more.",
				secondIcon: faDatabase,
				disabled: false,
				credits: "1",
				per: "Person",
			},
			{
				listType: ListSourceType.COMPANY_ENRICH,
				mode: CompanyEnrichType.LOOK_A_LIKE,
				title: "Find lookalike companies",
				description: "Start from companies you already know and find similar ones",
				secondIcon: faDatabase,
				disabled: false,
				credits: "1",
				per: "Person",
			},
			{
				listType: ListSourceType.COMPANY_ENRICH,
				mode: CompanyEnrichType.NEW_IN_ROLE,
				title: "Find people that started a new in role",
				description: "Find people that changed roles or employers",
				secondIcon: faDatabase,
				disabled: false,
				credits: "1",
				per: "Person",
			},
			{
				listType: ListSourceType.LI_PEOPLE,
				mode: LiPeopleType.SPECIFIC,
				title: "Import from a Linkedin Search",
				description: "Paste a LinkedIn search URLs to import the search results",
				icon: Person,
				disabled: false,
				credits: "1",
				per: "Person",
				tags: liPeopleTags,
			},
			{
				listType: ListSourceType.LI_SN,
				mode: LiSnType.SPECIFIC,
				title: "Import from Linkedin Sales Navigator",
				description: "Paste a Linkedin Sales Navigator search URL to import the search results",
				icon: LISN,
				disabled: false,
				credits: "1",
				per: "Person",
				tags: liSnTags,
			},
			{
				listType: ListSourceType.MAPS,
				title: "Import Google Maps",
				description: "Find local businesses on Google Maps and find relevant leads at those",
				icon: MapIcon,
				credits: "1",
				per: "Person",
				subType: ScrapeLevel.GET_ENRICHMENT,
			},
			{
				listType: ListSourceType.FUNDING_ROUNDS,
				mode: FundingMaType.FUNDING,
				title: "Track startup funding rounds",
				description:
					"Search our global database for startups that recently raised, plus relevant leads at each.",
				icon: Exhibitors,
				credits: "2",
				per: "Company (incl. 1 person)",
				subPer: "+1 credit per additional person",
			},
			{
				listType: ListSourceType.FUNDING_ROUNDS,
				mode: FundingMaType.MA,
				title: "Track Mergers & Acquisitions",
				description:
					"Search our global database for companies that merged or were acquired, plus relevant leads.",
				icon: Exhibitors,
				credits: "2",
				per: "Company (incl. 1 person)",
				subPer: "+1 credit per additional person",
			},
			{
				listType: ListSourceType.PARALLEL_AI,
				mode: ParallelAiType.SEARCH,
				title: "Find companies with natural language",
				description:
					"Describe the companies you want in plain language and search for relevant leads",
				secondIcon: faSparkles,
				credits: "2",
				per: "Company (incl. 1 person)",
				subPer: "+1 credit per additional person",
				subType: ScrapeLevel.GET_ENRICHMENT,
			},
			{
				listType: ListSourceType.PARALLEL_AI,
				mode: ParallelAiType.MONITOR,
				title: "Monitor company news and announcements",
				description:
					"Describe the news and announcements to watch, and we'll surface matching companies and relevant leads",
				secondIcon: faSparkles,
				credits: "2",
				per: "Company (incl. 1 person)",
				subPer: "+1 credit per additional person",
				subType: ScrapeLevel.GET_ENRICHMENT,
			},
		],
	},
	{
		title: "Track open jobs",
		description: "Find companies that are hiring",
		icon: JobSource,
		items: [
			{
				listType: ListSourceType.JOBS,
				title: "Find companies that are hiring",
				description:
					"Find companies hiring for specific roles on job portals like LinkedIn Jobs, plus relevant leads there",
				icon: JobOffer,
				credits: "1",
				per: "Person",
				subType: ScrapeLevel.GET_ENRICHMENT,
			},
		],
	},

	{
		title: "Profile Tracker",
		description: "Monitor LinkedIn profiles for activity, engagement and job change signals",
		icon: Person,
		requires: "TRACKER_AS_LIST",
		items: [
			{
				listType: ListSourceType.TRACKER,
				title: "Monitor target accounts for custom signals",
				description:
					"Upload people or companies and watch them for the signals you define, across LinkedIn, the web and employee changes.",
				icon: Person,
				credits: "2",
				per: "Company (incl. 1 person)",
				subPer: "+0,1 credit per additional person",
				subType: CsvEnrichmentType.PERSON,
			},
			{
				listType: ListSourceType.JOB_CHANGE,
				title: "Monitor my contacts for job changes",
				description:
					"Upload people to watch and we'll flag job changes and new roles as they happen.",
				icon: Person,
				credits: "1",
				per: "Person initially",
				subPer: "0,1 credits/Person per repeated lookup",
				subType: CsvEnrichmentType.PERSON,
			},
		],
	},
	{
		title: "Re-engage existing contacts",
		description: "Create a smart list of existing contacts to re-engage.",
		icon: Person,
		requires: "SMART_LIST",
		items: [
			{
				listType: ListSourceType.SMART_LIST,
				title: "Re-engage past contacts",
				description:
					"Build a smart list of contacts you've spoken with before and reach them again.",
				icon: Person,
				credits: "1",
				per: "Person",
			},
		],
	},
	{
		title: "Empty list",
		description: "Create an empty list. You can manually add people to this list.",
		secondIcon: faFile,
		items: [
			{
				listType: ListSourceType.EMPTY,
				title: "Empty list",
				description: "Start blank and add people manually or from your other lists.",
				credits: "1",
				per: "Person",
				secondIcon: faFile,
				subType: ScrapeLevel.GET_ENRICHMENT,
			},
		],
	},
];
